import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaDownload, FaPrint, FaShare, FaArrowLeft, FaSearch, FaSearchMinus, FaSearchPlus } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import html2canvas from 'html2canvas';

const CertificatePage = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [certificateData, setCertificateData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const certificateRef = useRef(null);
  const containerRef = useRef(null);
  
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const CERTIFICATE_WIDTH = 900;
  const CERTIFICATE_HEIGHT = 630;

  useEffect(() => {
    const fetchCertificateData = () => {
      setIsLoading(true);
      
      const testResults = location.state?.testResults;
      
      setTimeout(() => {
        const mockData = {
          testTitle: testResults?.testTitle || "اختبار الرياضيات المتقدم",
          score: testResults?.score || 85,
          date: new Date().toLocaleDateString('ar-SA'),
          certificateId: `CRT-${Math.floor(Math.random() * 10000)}`,
          userName: user?.name || user?.fullname || "مستخدم",
          userClass: user?.class || "غير محدد",
          userSchool: user?.school || "غير محدد",
          duration: testResults?.duration || "45 دقيقة",
          level: testResults?.level || "متقدم",
          completionDate: new Date().toLocaleDateString('ar-SA', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          correctAnswers: testResults?.correctAnswers || 0,
          totalQuestions: testResults?.totalQuestions || 0
        };
        setCertificateData(mockData);
        setIsLoading(false);
      }, 1000);
    };

     const initializeScale = () => {
      const containerWidth = window.innerWidth - 32;  
      const containerHeight = window.innerHeight - 200; 
      
       const widthScale = containerWidth / CERTIFICATE_WIDTH;
      const heightScale = containerHeight / CERTIFICATE_HEIGHT;
      
       const idealScale = Math.min(widthScale, heightScale, 0.9);  
      setScale(idealScale);
    };

    if (user) fetchCertificateData();
    initializeScale();
    
    window.addEventListener('resize', initializeScale);
    return () => window.removeEventListener('resize', initializeScale);
  }, [user, testId, location.state]);

   const handleBackToResults = () => {
     localStorage.setItem('returnToResults', 'true');
     navigate(-1);
  };

  const handleDownload = async () => {
    if (certificateRef.current) {
       const originalScale = scale;
      const originalPosition = {...position};
      
      setScale(1);
      setPosition({ x: 0, y: 0 });
      
       setTimeout(async () => {
        const canvas = await html2canvas(certificateRef.current, {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
        });
        
         setScale(originalScale);
        setPosition(originalPosition);
        
        const link = document.createElement('a');
        link.download = `شهادة-${certificateData.certificateId}.png`;
        link.href = canvas.toDataURL('image/png', 1.0);
        link.click();
      }, 100);
    }
  };

  const handlePrint = () => {
    const certificateElement = certificateRef.current;
    if (certificateElement) {
       const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>شهادة ${certificateData.testTitle}</title>
            <style>
              @media print {
                body { margin: 0; padding: 0; }
                .certificate-print { width: 100%; height: auto; page-break-inside: avoid; }
              }
              body { 
                margin: 0; 
                padding: 20px; 
                background: white; 
                display: flex; 
                justify-content: center; 
                align-items: center; 
                min-height: 100vh; 
                font-family: 'Arial', sans-serif; 
              }
              .certificate-print { 
                width: ${CERTIFICATE_WIDTH}px; 
                height: ${CERTIFICATE_HEIGHT}px; 
                background: white; 
                position: relative;
                background: linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #ffffff 100%);
                border: 1px solid #f1f5f9;
                box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.8);
              }
            </style>
          </head>
          <body>
            ${certificateElement.outerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: `شهادة إتمام - ${certificateData.testTitle}`,
        text: `لقد حصلت على شهادة في ${certificateData.testTitle} بنتيجة ${certificateData.score}%`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('تم نسخ رابط الشهادة!');
    }
  };
  
   const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.1, 3));
  };
  
  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.1, 0.3));
  };
  
  const handleResetZoom = () => {
     const containerWidth = containerRef.current?.clientWidth || window.innerWidth - 32;
    const containerHeight = containerRef.current?.clientHeight || window.innerHeight - 200;
    
    const widthScale = containerWidth / CERTIFICATE_WIDTH;
    const heightScale = containerHeight / CERTIFICATE_HEIGHT;
    
    setScale(Math.min(widthScale, heightScale, 0.9));  
    setPosition({ x: 0, y: 0 });
  };
  
  const handleMouseDown = useCallback((e) => {
    setDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  }, [position]);
  
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      setDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  }, [position]);
  
  const handleMouseMove = useCallback((e) => {
    if (dragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [dragging, dragStart]);
  
  const handleTouchMove = useCallback((e) => {
    if (dragging && e.touches.length === 1) {
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
      e.preventDefault();
    }
  }, [dragging, dragStart]);
  
  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);
  
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="text-5xl text-gray-400 mb-3">🎓</div>
          <h2 className="text-xl font-bold text-gray-700 mb-2">يجب تسجيل الدخول</h2>
          <button onClick={() => navigate('/login')} className="bg-[#320A6B] text-white px-5 py-2 rounded-lg hover:bg-[#2a095a] text-sm">
            تسجيل الدخول
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-b-2 border-[#320A6B] mx-auto mb-3 rounded-full"></div>
          <p className="text-gray-600 text-sm">جاري تحميل الشهادة...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-2 sm:py-4">
       <div className="container mx-auto px-2 sm:px-3 mb-2">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-3 mb-2">
          <button
            onClick={handleBackToResults}
            className="flex items-center text-[#320A6B] hover:text-[#2a095a] transition-colors bg-white px-3 py-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 w-full sm:w-auto justify-center text-xs sm:text-sm"
          >
            <FaArrowLeft className="ml-1.5" />
            العودة للاختبارات
          </button>
          
          <div className="flex flex-wrap justify-center gap-2 w-full sm:w-auto">
            <button
              onClick={handleDownload}
              className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-3 py-1.5 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-300 flex items-center shadow-md hover:shadow-lg w-full sm:w-auto justify-center text-xs sm:text-sm"
            >
              <FaDownload className="ml-1.5" />
              تنزيل الشهادة
            </button>
            <button
              onClick={handlePrint}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-3 py-1.5 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 flex items-center shadow-md hover:shadow-lg w-full sm:w-auto justify-center text-xs sm:text-sm"
            >
              <FaPrint className="ml-1.5" />
              طباعة الشهادة
            </button>
            <button
              onClick={handleShare}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-1.5 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 flex items-center shadow-md hover:shadow-lg w-full sm:w-auto justify-center text-xs sm:text-sm"
            >
              <FaShare className="ml-1.5" />
              مشاركة الشهادة
            </button>
          </div>
        </div>
        
         <div className="flex justify-center gap-2 mb-2">
          <button 
            onClick={handleZoomIn}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-1.5 rounded-lg"
            aria-label="تكبير"
          >
            <FaSearchPlus size={14} />
          </button>
          <button 
            onClick={handleResetZoom}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-1.5 rounded-lg"
            aria-label="إعادة ضبط الحجم"
          >
            <FaSearch size={14} />
          </button>
          <button 
            onClick={handleZoomOut}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-1.5 rounded-lg"
            aria-label="تصغير"
          >
            <FaSearchMinus size={14} />
          </button>
        </div>
      </div>

       <div 
        ref={containerRef}
        className="overflow-hidden mx-auto relative bg-gray-100 rounded-xl border border-gray-200"
        style={{ 
          width: 'calc(100% - 16px)', 
          height: 'calc(100vh - 140px)',
          maxHeight: '750px',
          maxWidth: '1100px',
          minHeight: '350px',
          cursor: dragging ? 'grabbing' : 'grab'
        }}
      >
         <div 
          ref={certificateRef}
          className="certificate-container absolute"
          style={{ 
            width: CERTIFICATE_WIDTH,
            height: CERTIFICATE_HEIGHT,
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            transformOrigin: '0 0',
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        >
           <div className="absolute inset-0 border-[14px] border-transparent">
            <div className="absolute inset-6 border-2 border-amber-300 rounded-lg"></div>
            <div className="absolute inset-8 border border-amber-100 rounded-lg"></div>
          </div>

 <div className="relative z-10 flex flex-col justify-between h-full px-16 py-14">
   <div className="flex justify-between items-start w-full text-base text-gray-700 font-light mb-5 mt-2">
     <div className="flex flex-col items-center">
      <img 
        src="/images/nafslogo.png" 
        alt="شعار المنصة" 
        className="w-16 h-16 object-contain mb-2" 
      />
      <div className="text-sm">
        رقم الشهادة:
        <span className="font-bold text-[#320A6B] mr-1">{certificateData.certificateId}</span>
      </div>
    </div>

     <div className="absolute left-1/2 top-20 transform -translate-x-1/2 text-center w-full">
      <div className="text-4xl font-bold text-amber-700 font-serif mb-1">
        شهادة تميز
      </div>
    </div>

     <div className="text-right flex flex-col items-end">
      <div className="text-xl font-bold text-[#320A6B] mb-1">منصة نافس برو</div>
      <div className="text-sm">
        التاريخ:
        <span className="font-bold text-[#320A6B] mr-1">{certificateData.completionDate}</span>
      </div>
    </div>
  </div>

   <div className="flex flex-col items-center justify-center text-center flex-1">
  
    
<div className="flex flex-col items-center mb-4">
  <div className="flex items-baseline justify-center gap-6 mb-3">
     <div className="text-3xl font-bold text-[#320A6B] font-serif border-b-2 border-amber-300 pb-2 px-2">
      {certificateData.userName}
    </div>

     <span className="text-3xl font-bold text-gray-700 font-serif">للطالب/ه</span>
  </div>
</div>

     <div className="flex justify-center gap-6 mb-4 text-base text-gray-700">
      <div className="bg-gray-50 px-3 py-1.5 rounded-lg">
        <span className="font-bold text-[#320A6B]">الصف:</span> {certificateData.userClass}
      </div>
      <div className="bg-gray-50 px-3 py-1.5 rounded-lg">
        <span className="font-bold text-[#320A6B]">المدرسة:</span> {certificateData.userSchool}
      </div>
    </div>

    <div className="text-base text-gray-700 font-light mb-4">لإنجازه المتميز في</div>

    <div className="text-2xl font-bold text-gray-800 bg-gradient-to-r from-amber-50 to-purple-50 py-4 px-8 rounded-xl border-2 border-amber-100 shadow-lg inline-block mb-8">
      {certificateData.testTitle}
    </div>

 ب    <div className="grid grid-cols-4 gap-4 mb-6 w-full max-w-2xl">
      <div>
        <div className="text-xs text-gray-500 mb-1">النتيجة</div>
        <div className="text-2xl font-bold text-[#320A6B]">{certificateData.score}%</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">المستوى</div>
        <div className="text-lg font-bold text-[#320A6B]">{certificateData.level}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">المدة</div>
        <div className="text-base font-bold text-[#320A6B]">{certificateData.duration}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">الإجابات</div>
        <div className="text-base font-bold text-[#320A6B]">
          {certificateData.correctAnswers}/{certificateData.totalQuestions}
        </div>
      </div>
    </div>
  </div>
</div>
        </div>
      </div> 
      <style jsx>{`
        .certificate-container {
          background: linear-gradient(135deg, #ffffff 0%, #fefefe 50%, #ffffff 100%);
          border: 1px solid #f1f5f9;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.8);
        }
      `}</style>
    </div>
  );
};

export default CertificatePage;